"use strict"

/**
 * Applicant response to an information request.
 *
 * The server is the authority on what may change and whether everything was answered;
 * this only stops an obviously incomplete form leaving the browser, makes the consequences
 * clear before it does, and then sends the answers the way the submission wizard sends an
 * application: each document as its own single-file upload, then one JSON request with the
 * values and the ids those uploads returned.
 *
 * It used to be a plain browser form post. The firewall in front of the public site blocks
 * that shape - multipart body, indexed names like Fields[0].Value - while it lets the
 * wizard's requests through, so this page now uses exactly the wizard's requests.
 */
const InfoResponse = function () {
    const form = document.querySelector('#info-response-form')

    if (!form || form.dataset.hasRequests !== 'true') {
        return { init: function () { } }
    }

    const submit = form.querySelector('#info-response-submit')
    const submitUrl = form.dataset.submitUrl
    const uploadUrl = form.dataset.uploadUrl
    const statusUrl = form.dataset.statusUrl

    const setBusy = function (busy) {
        if (!submit) {
            return
        }
        submit.disabled = busy
        submit.setAttribute('aria-busy', busy ? 'true' : 'false')
        submit.textContent = busy ? 'Submitting...' : 'Submit my response'

        // Stays up through the redirect to the status page; only a failure takes it down
        if (typeof RbfPageLoader !== 'undefined') {
            busy ? RbfPageLoader.show('Submitting your response') : RbfPageLoader.hide()
        }
    }

    const showError = function (message) {
        Swal.fire({
            text: message || 'An error occurred while submitting your response. Please try again.',
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: 'Ok',
            customClass: { confirmButton: 'btn btn-primary' }
        })
    }

    const messageFromError = function (error) {
        if (error && error.response && error.response.status === 429) {
            return 'Too many requests. Please wait a moment and try again.'
        }
        if (error && error.response && typeof error.response.data === 'string' && error.response.data) {
            return error.response.data
        }
        if (typeof getMessageFromError === 'function') {
            return getMessageFromError(error)
        }
        return null
    }

    /**
     * The inputs keep the names the form post used - Fields[n].RequestUniqueId beside
     * Fields[n].Value or Fields[n].Values, and Files[n].RequestUniqueId beside
     * Files[n].File - so the Razor partials did not have to change. They are read here and
     * never sent as-is.
     */
    const collect = function () {
        const fields = []
        const files = []

        form.querySelectorAll('input[type="hidden"][name$=".RequestUniqueId"]').forEach(function (idInput) {
            const match = /^(Fields|Files)\[(\d+)\]\.RequestUniqueId$/.exec(idInput.name)
            if (!match) {
                return
            }

            const prefix = match[1] + '[' + match[2] + ']'

            if (match[1] === 'Fields') {
                const multi = form.querySelector('[name="' + prefix + '.Values"]')
                if (multi) {
                    const values = Array.from(multi.selectedOptions).map(function (option) { return option.value })
                    fields.push({ requestUniqueId: idInput.value, value: values.join(',') })
                    return
                }

                const single = form.querySelector('[name="' + prefix + '.Value"]')
                fields.push({ requestUniqueId: idInput.value, value: single ? single.value : '' })
                return
            }

            const fileInput = form.querySelector('[name="' + prefix + '.File"]')
            files.push({
                requestUniqueId: idInput.value,
                file: fileInput && fileInput.files.length > 0 ? fileInput.files[0] : null
            })
        })

        return { fields: fields, files: files }
    }

    /** One file per request, exactly as the wizard's document step uploads. */
    const uploadOne = function (entry) {
        const body = new FormData()
        body.append('file', entry.file, entry.file.name)

        return axios.post(uploadUrl, body, {
            headers: getRequestHeaders(),
            timeout: 120000
        }).then(function (response) {
            const data = response.data || {}
            if (!data.success || !data.attachmentId) {
                throw new Error(data.message || ('We could not upload ' + entry.file.name + '. Please try again.'))
            }
            return { requestUniqueId: entry.requestUniqueId, attachmentUniqueId: data.attachmentId }
        })
    }

    const send = function () {
        const answers = collect()

        const missingFile = answers.files.find(function (entry) { return !entry.file })
        if (missingFile) {
            showError('Please choose a file for every document requested.')
            setBusy(false)
            return
        }

        // Uploads run one after another so a slow connection is not asked to push every
        // document at once, and so the first failure stops the rest.
        let chain = Promise.resolve([])
        answers.files.forEach(function (entry) {
            chain = chain.then(function (uploaded) {
                return uploadOne(entry).then(function (result) {
                    uploaded.push(result)
                    return uploaded
                })
            })
        })

        chain.then(function (uploaded) {
            const payload = {
                refNum: form.querySelector('[name="RefNum"]').value,
                tin: form.querySelector('[name="Tin"]').value,
                fields: answers.fields,
                files: uploaded
            }

            return axios.post(submitUrl, payload, {
                headers: getJsonRequestHeaders(),
                timeout: 60000
            })
        }).then(function (response) {
            const data = response.data || {}
            if (!data.success) {
                throw new Error(data.message || 'Your response could not be submitted. Please try again.')
            }

            // The server has set the success banner; the status page shows it on load.
            window.location.assign(statusUrl)
        }).catch(function (error) {
            setBusy(false)
            showError((error && error.response) ? messageFromError(error) : (error && error.message))
        })
    }

    const handleSubmit = function () {
        form.addEventListener('submit', function (evt) {
            // Never a native post: the server no longer accepts one, and the firewall
            // would not pass it anyway.
            evt.preventDefault()

            const unanswered = Array.from(form.querySelectorAll('[required]'))
                .filter(field => !field.value)

            if (unanswered.length > 0) {
                unanswered[0].focus()
                return
            }

            Swal.fire({
                title: 'Submit your response?',
                text: 'Your application goes back to the Reserve Bank for review and these items can no longer be changed.',
                icon: 'question',
                showCancelButton: true,
                buttonsStyling: false,
                confirmButtonText: 'Yes, submit',
                cancelButtonText: 'Not yet',
                customClass: {
                    confirmButton: 'btn btn-primary',
                    cancelButton: 'btn btn-light'
                }
            }).then(result => {
                if (result.isConfirmed) {
                    setBusy(true)
                    send()
                }
            })
        })
    }

    return {
        init: function () {
            handleSubmit()
        }
    }
}()

document.addEventListener('DOMContentLoaded', function () {
    InfoResponse.init()
})
